import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@ludolio/shared-ui';
import { getErrorMessage, labsApi } from '@/lib/api';
import { formatDate } from '@/lib/format';
import type { LabShowcaseState, LabShowcaseStatus } from '@/lib/types';

const STATUS_LABELS: Record<LabShowcaseStatus, string> = {
  draft: 'Draft',
  in_review: 'In review',
  changes_requested: 'Changes requested',
  approved: 'Approved',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

const LOCKED = new Set<LabShowcaseStatus>(['in_review', 'approved']);
const WITHDRAWABLE = new Set<LabShowcaseStatus>(['in_review', 'changes_requested', 'approved']);

export function ShowcasePanel({ projectId, refreshKey }: { projectId: string; refreshKey: string }) {
  const [state, setState] = useState<LabShowcaseState | null>(null);
  const [software, setSoftware] = useState('MIT');
  const [asset, setAsset] = useState('CC-BY-4.0');
  const [declaration, setDeclaration] = useState('');
  const [publishSource, setPublishSource] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    labsApi.showcase(projectId)
      .then((next) => {
        if (cancelled) return;
        setState(next);
        if (next.submission) {
          setSoftware(next.submission.openSourceLicense);
          setAsset(next.submission.assetLicense);
          setDeclaration(next.submission.assetDeclaration ?? '');
          setPublishSource(next.submission.sourcePublished);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(getErrorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, refreshKey]);

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!state) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const status = state.submission?.status ?? null;
  const locked = status ? LOCKED.has(status) : false;
  const editable = state.canManage && !state.blocked && !locked;
  const payload = {
    openSourceLicense: software,
    assetLicense: asset,
    assetDeclaration: asset === 'ownership' ? declaration.trim() : null,
    sourcePublished: state.sourceLocked ? false : publishSource,
  };

  const apply = (next: LabShowcaseState) => {
    setState(next);
  };

  const save = async () => {
    if (!state.releaseId) return;
    setBusy(true);
    try {
      apply(await labsApi.saveShowcase(state.releaseId, payload));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!state.releaseId) return;
    setBusy(true);
    try {
      apply(await labsApi.submitShowcase(state.releaseId, payload));
      toast.success('Submitted for showcase review');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    if (!state.releaseId) return;
    setBusy(true);
    try {
      apply(await labsApi.withdrawShowcase(state.releaseId));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="grid gap-4 p-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">Global showcase</h2>
        {status && <span className="text-sm text-muted-foreground">{STATUS_LABELS[status]}</span>}
      </div>
      {state.blocked && <p className="text-sm text-muted-foreground">{state.blocked}</p>}
      {state.releaseId && <>
      {status === 'approved' && (
        <p className="text-sm text-muted-foreground">
          Publishing a new version removes this from the showcase until that version is approved.
        </p>
      )}
      {state.submission?.reviewNote && (
        <p className="whitespace-pre-line text-sm text-muted-foreground">{state.submission.reviewNote}</p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="grid gap-1.5">
          <Label htmlFor="showcase-software">Software licence</Label>
          <Select value={software} onValueChange={setSoftware} disabled={!editable || busy}>
            <SelectTrigger id="showcase-software">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {state.softwareLicenses.map((license) => (
                <SelectItem key={license.id} value={license.id}>{license.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="showcase-assets">Asset licence</Label>
          <Select value={asset} onValueChange={setAsset} disabled={!editable || busy}>
            <SelectTrigger id="showcase-assets">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {state.assetLicenses.map((license) => (
                <SelectItem key={license.id} value={license.id}>{license.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {asset === 'ownership' && (
        <div className="grid gap-1.5">
          <Label htmlFor="showcase-ownership">Ownership</Label>
          <Textarea
            id="showcase-ownership"
            value={declaration}
            onChange={(event) => setDeclaration(event.target.value)}
            disabled={!editable || busy}
            rows={3}
          />
        </div>
      )}
      {state.sourceLocked ? (
        <p className="text-sm text-muted-foreground">Remixed under university terms. Source stays in your university.</p>
      ) : (
        <div className="flex items-start gap-3">
          <Checkbox
            id="showcase-source"
            checked={publishSource}
            onCheckedChange={(value) => setPublishSource(value === true)}
            disabled={!editable || busy}
          />
          <Label htmlFor="showcase-source" className="font-normal leading-5">
            Publish the source package with this licence
          </Label>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {state.submission?.submittedAt && (
          <span className="mr-auto text-sm text-muted-foreground">
            Submitted {formatDate(state.submission.submittedAt)}
          </span>
        )}
        {state.canManage && status && WITHDRAWABLE.has(status) && (
          <Button type="button" variant="outline" disabled={busy} onClick={() => void withdraw()}>
            Withdraw
          </Button>
        )}
        {editable && (
          <>
            <Button type="button" variant="outline" disabled={busy} onClick={() => void save()}>
              Save
            </Button>
            <Button type="button" disabled={busy} onClick={() => void submit()}>
              Submit
            </Button>
          </>
        )}
      </div>
      </>}
    </Card>
  );
}
